import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PriceInputContextType {
  // State values
  buyPrice: string;
  sellPrice: string;
  showBuyInput: boolean;
  showSellInput: boolean;
  // Actions
  setBuyPrice: (price: string) => void;
  setSellPrice: (price: string) => void;
  fillBuyPrice: (price: string) => void;
  fillSellPrice: (price: string) => void;
  setBuyPriceInputVisible: (visible: boolean) => void;
  setSellPriceInputVisible: (visible: boolean) => void;
}

const PriceInputContext = createContext<PriceInputContextType | undefined>(undefined);

export const usePriceInput = () => {
  const context = useContext(PriceInputContext);
  if (!context) {
    throw new Error('usePriceInput must be used within a PriceInputProvider');
  }
  return context;
};

interface PriceInputProviderProps {
  children: ReactNode;
}

export const PriceInputProvider: React.FC<PriceInputProviderProps> = ({ children }) => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [showBuyInput, setShowBuyInput] = useState(false);
  const [showSellInput, setShowSellInput] = useState(false);

  const fillBuyPrice = useCallback((price: string) => {
    setBuyPrice(price);
    setShowBuyInput(true);
  }, []);

  const fillSellPrice = useCallback((price: string) => {
    setSellPrice(price);
    setShowSellInput(true);
  }, []);

  const setBuyPriceInputVisible = useCallback((visible: boolean) => {
    setShowBuyInput(visible);
    if (!visible) {
      setBuyPrice('');
    }
  }, []);

  const setSellPriceInputVisible = useCallback((visible: boolean) => {
    setShowSellInput(visible);
    if (!visible) {
      setSellPrice('');
    }
  }, []);

  const value: PriceInputContextType = {
    // State values
    buyPrice,
    sellPrice,
    showBuyInput,
    showSellInput,
    // Actions
    setBuyPrice,
    setSellPrice,
    fillBuyPrice,
    fillSellPrice,
    setBuyPriceInputVisible,
    setSellPriceInputVisible,
  };

  return (
    <PriceInputContext.Provider value={value}>
      {children}
    </PriceInputContext.Provider>
  );
};
